import type { PropsWithChildren } from 'react';

import { Box, Card, TextButton } from '@zillow/constellation-10';
import { LoadingHeading, LoadingText } from '@zg-rentals/loading-components';

import FaqAccordion from '../../Widgets/FaqAccordion';
import ContactAnswer from '../TenantContentContainer/ContactAnswer';
import type { FaqSection } from '../../../models/Types';
import { staticAssetLinkProps } from '../../../utils/staticAsssets';
import { Lease } from '../../../models';

type Props = PropsWithChildren<{
  explainerLeaseName?: string;
  isLeaseRenewal?: boolean;
  isLoading?: boolean;
}>;

export default function ColandlordFaq({ explainerLeaseName, children, isLeaseRenewal, isLoading = false }: Props) {
  if (isLoading) {
    return (
      <>
        {children}
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tight' }}>
          <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tighter' }}>
            <LoadingText isLoading loadingWidth="12rem">
              Loading
            </LoadingText>
            <LoadingText isLoading loadingWidth="5rem">
              Loading
            </LoadingText>
          </Box>
          <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tighter' }}>
            <LoadingText isLoading loadingWidth="11rem">
              Loading
            </LoadingText>
            <LoadingText isLoading loadingWidth="6rem">
              Loading
            </LoadingText>
          </Box>
          <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tighter' }}>
            <LoadingText isLoading loadingWidth="13rem">
              Loading
            </LoadingText>
            <LoadingText isLoading loadingWidth="5.5rem">
              Loading
            </LoadingText>
          </Box>
        </Box>
        <LoadingHeading
          isLoading
          level={3}
          loadingWidth="60%"
          css={{
            paddingBottom: 'xs',
            textStyle:
              'responsive.body-lg-bold' /* oxlint-disable-line constellation/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
            marginTop: 'tighter',
            marginBottom: 'tighter',
          }}
        >
          Loading
        </LoadingHeading>
        {[1, 2, 3, 4, 5].map((index) => (
          <Card key={index} tone="neutral" elevated={false} css={{ marginBottom: 'tighter' }}>
            <LoadingHeading
              isLoading
              level={3}
              loadingWidth="70%"
              css={{
                textStyle:
                  'responsive.body-lg-bold' /* oxlint-disable-line constellation/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
              }}
            >
              Loading
            </LoadingHeading>
          </Card>
        ))}
      </>
    );
  }

  function getFAQsections(): Array<FaqSection> {
    const sections: Array<FaqSection> = isLeaseRenewal
      ? [
          {
            label: 'Why should I review the lease renewal agreement before signing?',
            description:
              'Your co-landlord has created this lease renewal agreement and asked your tenants to sign it. You’ll be ' +
              'asked to sign it once they‘re done. Before the lease renewal agreement gets ratified, it‘s important that you review it ' +
              'and ask your co-landlord to make any necessary updates.',
          },
          {
            label: 'What if I need changes made to the lease renewal agreement?',
            description:
              'Reach out to your co-landlord directly if you would like updates made to your lease renewal agreement. ' +
              'They can make the changes and send the updated lease renewal agreement out to you and your tenants for review ' +
              'or signing.',
          },
          {
            label: 'Why isn’t this lease renewal agreement on my Zillow Rental Manager dashboard?',
            description:
              'Since your co-landlord set up this lease renewal agreement, the property appears on their Zillow ' +
              'Rental Manager dashboard. Right now, only one account at a time can have dashboard access to a property.',
          },
          {
            label: 'How do I download or print this lease renewal agreement?',
            description:
              'When reviewing the lease renewal agreement, you can download an un-signable draft directly ' +
              'from your web browser. Once everyone signs your lease renewal agreement, you can download a ' +
              'signed copy anytime.',
          },
        ]
      : [
          {
            label: 'Why should I review the lease before signing?',
            description:
              'Your co-landlord has created this lease and asked your tenants to sign it. You’ll be ' +
              'asked to sign it once they‘re done. Before the lease gets ratified, it‘s important that you review it ' +
              'and ask your co-landlord to make any necessary updates.',
          },
          {
            label: 'What if I need changes made to the lease?',
            description:
              'Reach out to your co-landlord directly if you would like updates made to your lease. ' +
              'They can make the changes and send the updated lease out to you and your tenants for review ' +
              'or signing.',
          },
          {
            label: 'Why isn’t this lease on my Zillow Rental Manager dashboard?',
            description:
              'Since your co-landlord set up this lease, the property appears on their Zillow ' +
              'Rental Manager dashboard. Right now, only one account at a time can have dashboard access to a property.',
          },
          {
            label: 'How do I download or print this lease?',
            description:
              'When reviewing the lease, you can download an un-signable draft directly ' +
              'from your web browser. Once everyone signs your lease, you can download a ' +
              'signed copy anytime.',
          },
        ];

    if (explainerLeaseName && !isLeaseRenewal) {
      sections.push({
        label: 'Want clarifications for lease rules?',
        description: (
          <TextButton asChild>
            <a {...staticAssetLinkProps(Lease.getExplainerLeasePath(explainerLeaseName))}>See our explainer lease</a>
          </TextButton>
        ),
      });
    }

    sections.push({
      label: 'Still have questions? Contact us',
      description: <ContactAnswer />,
    });

    return sections;
  }

  return (
    <FaqAccordion sections={getFAQsections()} title="You may be wondering…">
      {children}
    </FaqAccordion>
  );
}
